import * as helios from "@hyperionbt/helios";
import {
  CardanoNetwork,
  DatumArtwork,
  KreateUTxO,
} from "kreate-common/modules/business-types";

import ScriptArtwork from "../../onchain/scriptArtwork.hl";
import {
  addHeliosCollateral,
  getCardanoNetworkParams,
  toHeliosDatum,
  toHeliosUTxO,
} from "../../utils";

type Params = {
  network: CardanoNetwork;
  wallet: helios.Cip30Wallet;
  artworkUTxO: KreateUTxO;
  newArtworkDatum: DatumArtwork;
};

export async function buildPartialTx$UpdateArtwork({
  network,
  wallet,
  artworkUTxO,
  newArtworkDatum,
}: Params) {
  const walletHelper = new helios.WalletHelper(wallet);

  const address = await walletHelper.baseAddress;
  const [utxos] = await walletHelper.pickUtxos(
    new helios.Value(BigInt(2_000_000)),
    helios.CoinSelection.selectLargestFirst
  );

  const ownerPKH = address.pubKeyHash;
  const artworkHeliosUTxO = toHeliosUTxO(artworkUTxO);

  const tx = new helios.Tx();
  await addHeliosCollateral(wallet, tx);

  tx.addInputs(utxos);
  tx.addInput(artworkHeliosUTxO, new helios.ConstrData(0, []));

  tx.addOutput(
    new helios.TxOutput(
      artworkHeliosUTxO.origOutput.address,
      // We must "copy" a new value here.
      // Referencing will balance incorrectly if the new value is updated
      // by Helios to fit min lovelace from the changed datum size,
      // which will also slyly update the value in the input UTxO!
      helios.Value.fromCbor(artworkHeliosUTxO.origOutput.value.toCbor()),
      toHeliosDatum({ type: "artwork", value: newArtworkDatum })
    )
  );

  const scriptArtwork = new ScriptArtwork().compile(true);
  tx.attachScript(scriptArtwork);

  tx.addSigner(ownerPKH);

  await tx.finalize(getCardanoNetworkParams(network), address);
  return { tx };
}

/** @deprecated use `buildPartialTx$UpdateArtwork` instead */
export default async function updateArtwork(
  network: CardanoNetwork,
  wallet: helios.Cip30Wallet,
  artworkUTxO: KreateUTxO,
  newArtworkDatum: DatumArtwork
): Promise<helios.TxId> {
  const { tx } = await buildPartialTx$UpdateArtwork({
    network,
    wallet,
    artworkUTxO,
    newArtworkDatum,
  });

  const signatures = await wallet.signTx(tx);
  tx.addSignatures(signatures);

  return wallet.submitTx(tx);
}
